import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import NumberInput from '../../components/common/NumberInput';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useConnections } from '../../hooks/useConnections';
import api from '../../api/enhancedApiService';

const ValidationIssueSettings = () => {
  const [searchParams] = useSearchParams();
  const connectionId = searchParams.get('connection');
  
  const { connections, loading: connectionsLoading } = useConnections();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);

  // Form state for new/edit configuration
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    validation_type: '*',
    severity_filter: '*',
    table_pattern: '*',
    trigger_action: 'create_issue',
    severity_mapping: 'as_is',
    connection_id: connectionId || '',
    is_active: true,
    auto_create_issue: true,
    severity_override: '',
    priority_boost: 0,
    consecutive_failures_threshold: 1,
    failure_percentage_threshold: null,
    time_window_hours: 24,
    group_by_table: true,
    group_by_rule: true,
    max_issues_per_day: 10,
    notify_on_creation: true,
    notification_channels: ['in_app'],
    custom_notification_message: ''
  });

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/validation-issue-configs';
      if (connectionId) {
        url += `?connection_id=${connectionId}`;
      }

      const response = await api({
        url: url,
        method: 'GET'
      });

      setConfigs(response.configs || []);
    } catch (err) {
      console.error('Error loading configs:', err);
      setError(err.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadConfigs();
  }, [connectionId, loadConfigs]);

  // Set default connection when connections load
  useEffect(() => {
    if (connections.length > 0 && !formData.connection_id) {
      // Use connection from URL, or default to first connection
      const defaultConnectionId = connectionId || connections[0]?.id || '';
      setFormData(prev => ({ ...prev, connection_id: defaultConnectionId }));
    }
  }, [connections, connectionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (editingConfig) {
        // Update existing config
        await api({
          url: `/validation-issue-configs/${editingConfig.id}`,
          method: 'PUT',
          data: formData
        });
      } else {
        // Create new config
        await api({
          url: '/validation-issue-configs',
          method: 'POST',
          data: formData
        });
      }

      await loadConfigs();
      resetForm();
      setShowAddForm(false);
      setEditingConfig(null);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setFormData({
      name: config.name || '',
      description: config.description || '',
      validation_type: config.validation_type || '*',
      severity_filter: config.severity_filter || '*',
      table_pattern: config.table_pattern || '*',
      trigger_action: config.trigger_action || 'create_issue',
      severity_mapping: config.severity_mapping || 'as_is',
      connection_id: config.connection_id || '',
      is_active: config.is_active !== undefined ? config.is_active : true,
      auto_create_issue: config.auto_create_issue !== undefined ? config.auto_create_issue : true,
      severity_override: config.severity_override || '',
      priority_boost: config.priority_boost || 0,
      consecutive_failures_threshold: config.consecutive_failures_threshold || 1,
      failure_percentage_threshold: config.failure_percentage_threshold,
      time_window_hours: config.time_window_hours || 24,
      group_by_table: config.group_by_table !== undefined ? config.group_by_table : true,
      group_by_rule: config.group_by_rule !== undefined ? config.group_by_rule : true,
      max_issues_per_day: config.max_issues_per_day || 10,
      notify_on_creation: config.notify_on_creation !== undefined ? config.notify_on_creation : true,
      notification_channels: config.notification_channels || ['in_app'],
      custom_notification_message: config.custom_notification_message || ''
    });
    setEditingConfig(config);
    setShowAddForm(true);
  };

  const handleDelete = (config) => {
    setConfigToDelete(config);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!configToDelete) return;

    try {
      await api({
        url: `/validation-issue-configs/${configToDelete.id}`,
        method: 'DELETE'
      });

      await loadConfigs();
      setShowDeleteModal(false);
      setConfigToDelete(null);
    } catch (err) {
      console.error('Error deleting config:', err);
      setError(err.message || 'Failed to delete configuration');
    }
  };

  const resetForm = () => {
    // Default to connection from URL, or first available connection
    const defaultConnectionId = connectionId || connections[0]?.id || '';

    setFormData({
      name: '',
      description: '',
      validation_type: '*',
      severity_filter: '*',
      table_pattern: '*',
      trigger_action: 'create_issue',
      severity_mapping: 'as_is',
      connection_id: defaultConnectionId,
      is_active: true,
      auto_create_issue: true,
      severity_override: '',
      priority_boost: 0,
      consecutive_failures_threshold: 1,
      failure_percentage_threshold: null,
      time_window_hours: 24,
      group_by_table: true,
      group_by_rule: true,
      max_issues_per_day: 10,
      notify_on_creation: true,
      notification_channels: ['in_app'],
      custom_notification_message: ''
    });
  };


  const getScopeDescription = (config) => {
    const parts = [];
    if (config.validation_type && config.validation_type !== '*') {
      parts.push(`Type: ${config.validation_type}`);
    }
    if (config.severity_filter && config.severity_filter !== '*') {
      parts.push(`Severity: ${config.severity_filter}`);
    }
    if (config.table_pattern && config.table_pattern !== '*') {
      parts.push(`Tables: ${config.table_pattern}`);
    }
    return parts.length > 0 ? parts.join(', ') : 'All validations';
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-medium text-secondary-900 mb-2">
          Validation Issue Configuration
        </h2>
        <p className="text-sm text-secondary-600">
          Configure when validation failures should automatically create issues for tracking and resolution.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-danger-500 mr-2" />
            <p className="text-danger-700">{error}</p>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-8 bg-white border border-secondary-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            {editingConfig ? 'Edit Configuration' : 'Add New Configuration'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Configuration Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g., Critical Validation Failures"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Trigger Action *
                </label>
                <select
                  value={formData.trigger_action}
                  onChange={(e) => setFormData(prev => ({ ...prev, trigger_action: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  required
                >
                  <option value="create_issue">Create Issue</option>
                  <option value="notify_only">Notify Only</option>
                  <option value="ignore">Ignore</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Connection {formData.table_pattern && formData.table_pattern !== '*' && !formData.table_pattern.includes('*') && ' *'}
              </label>
              <select
                value={formData.connection_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, connection_id: e.target.value }))}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                required={formData.table_pattern && formData.table_pattern !== '*' && !formData.table_pattern.includes('*')}
              >
                <option value="">All Connections (for wildcard patterns only)</option>
                {connections.map(conn => {
                  const isFromUrl = connectionId && conn.id === connectionId;
                  const isCurrent = conn.id === formData.connection_id;
                  const label = conn.name || conn.connection_details?.database || 'Unnamed Connection';
                  return (
                    <option key={conn.id} value={conn.id}>
                      {label}{isCurrent ? ' (Current)' : ''}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-secondary-500 mt-1">
                {connectionId ? (
                  <span className="text-primary-600">
                    Pre-selected from current page. Change if needed.
                  </span>
                ) : formData.connection_id && connections.length > 0 ? (
                  <span>
                    Default connection selected. {formData.table_pattern && formData.table_pattern !== '*' && !formData.table_pattern.includes('*') ? 'Required for exact table names.' : 'Change if needed.'}
                  </span>
                ) : (
                  formData.table_pattern && formData.table_pattern !== '*' && !formData.table_pattern.includes('*')
                    ? 'Required when specifying an exact table name'
                    : 'Optional for wildcard patterns like * or customer*'
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                rows="2"
                placeholder="Describe when this configuration should apply"
              />
            </div>

            {/* Pattern Matching */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-secondary-900 mb-4">Pattern Matching</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Validation Type
                  </label>
                  <input
                    type="text"
                    value={formData.validation_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, validation_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="* for all, or specific type"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Use * for all types, or specify like 'null_check', 'range_check'
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Severity Filter
                  </label>
                  <select
                    value={formData.severity_filter}
                    onChange={(e) => setFormData(prev => ({ ...prev, severity_filter: e.target.value }))}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="*">All severities</option>
                    <option value="critical">Critical only</option>
                    <option value="high">High only</option>
                    <option value="medium">Medium only</option>
                    <option value="low">Low only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Table Pattern
                  </label>
                  <input
                    type="text"
                    value={formData.table_pattern}
                    onChange={(e) => setFormData(prev => ({ ...prev, table_pattern: e.target.value }))}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="* for all, customer*, *_staging"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Use * for all tables, customer* for prefix, *_staging for suffix
                  </p>
                </div>
              </div>
            </div>

            {/* Severity Mapping */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-secondary-900 mb-4">Severity Mapping</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Severity Mapping
                  </label>
                  <select
                    value={formData.severity_mapping}
                    onChange={(e) => setFormData(prev => ({ ...prev, severity_mapping: e.target.value }))}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="as_is">Use validation severity as-is</option>
                    <option value="escalate">Escalate (bump up one level)</option>
                    <option value="custom">Custom mapping</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Severity Override
                  </label>
                  <select
                    value={formData.severity_override}
                    onChange={(e) => setFormData(prev => ({ ...prev, severity_override: e.target.value }))}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">Use severity mapping</option>
                    <option value="low">Force Low</option>
                    <option value="medium">Force Medium</option>
                    <option value="high">Force High</option>
                    <option value="critical">Force Critical</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Threshold Configuration */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-secondary-900 mb-4">Threshold Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Consecutive Failures Threshold
                  </label>
                  <NumberInput
                    min={1}
                    max={10}
                    value={formData.consecutive_failures_threshold}
                    onChange={(e) => setFormData(prev => ({ ...prev, consecutive_failures_threshold: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Number of consecutive failures before creating an issue
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Failure Percentage Threshold (%)
                  </label>
                  <NumberInput
                    min={0}
                    max={100}
                    step={0.1}
                    value={formData.failure_percentage_threshold || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      failure_percentage_threshold: e.target.value ? parseFloat(e.target.value) : null 
                    }))}
                    className="w-full"
                    placeholder="Optional - leave empty to ignore"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Percentage of failed runs in time window to trigger issue
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Time Window (hours)
                  </label>
                  <NumberInput
                    min={1}
                    max={168}
                    value={formData.time_window_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, time_window_hours: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Time window for percentage calculation (1-168 hours)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Max Issues Per Day
                  </label>
                  <NumberInput
                    min={1}
                    max={100}
                    value={formData.max_issues_per_day}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_issues_per_day: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Rate limit to prevent issue spam
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Priority Boost
                </label>
                <NumberInput
                  min={0}
                  max={50}
                  value={formData.priority_boost}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority_boost: parseFloat(e.target.value) }))}
                  className="w-full"
                />
                <p className="text-xs text-secondary-500 mt-1">
                  Additional priority points for issues from this config
                </p>
              </div>
            </div>

            {/* Options */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-secondary-900 mb-4">Options</h4>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="is_active"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className=""
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-secondary-700">
                    Configuration is active
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="auto_create_issue"
                    type="checkbox"
                    checked={formData.auto_create_issue}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_create_issue: e.target.checked }))}
                    className=""
                  />
                  <label htmlFor="auto_create_issue" className="ml-2 text-sm text-secondary-700">
                    Automatically create issues when thresholds are met
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="group_by_table"
                    type="checkbox"
                    checked={formData.group_by_table}
                    onChange={(e) => setFormData(prev => ({ ...prev, group_by_table: e.target.checked }))}
                    className=""
                  />
                  <label htmlFor="group_by_table" className="ml-2 text-sm text-secondary-700">
                    Group issues by table (avoid duplicate issues for same table)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="group_by_rule"
                    type="checkbox"
                    checked={formData.group_by_rule}
                    onChange={(e) => setFormData(prev => ({ ...prev, group_by_rule: e.target.checked }))}
                    className=""
                  />
                  <label htmlFor="group_by_rule" className="ml-2 text-sm text-secondary-700">
                    Group issues by validation rule (avoid duplicate issues for same rule)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="notify_on_creation"
                    type="checkbox"
                    checked={formData.notify_on_creation}
                    onChange={(e) => setFormData(prev => ({ ...prev, notify_on_creation: e.target.checked }))}
                    className=""
                  />
                  <label htmlFor="notify_on_creation" className="ml-2 text-sm text-secondary-700">
                    Send notifications when issues are created
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingConfig(null);
                  resetForm();
                }}
                className="px-4 py-2 text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
              >
                {saving && <LoadingSpinner size="xs" className="mr-2" />}
                {editingConfig ? 'Update' : 'Create'} Configuration
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Configurations List */}
      <div className="bg-white border border-secondary-200 rounded-lg">
        <div className="px-6 py-4 border-b border-secondary-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-secondary-900">
              Validation-to-Issue Configurations
            </h2>
            <button
              onClick={() => {
                resetForm();
                setEditingConfig(null);
                setShowAddForm(true);
              }}
              className="flex items-center px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Configuration
            </button>
          </div>
        </div>

        {configs.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <InformationCircleIcon className="mx-auto h-12 w-12 text-secondary-400 mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              No configurations yet
            </h3>
            <p className="text-secondary-600 mb-4">
              Create your first validation-to-issue configuration to automatically track validation failures.
            </p>
            <button
              onClick={() => {
                resetForm();
                setEditingConfig(null);
                setShowAddForm(true);
              }}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Configuration
            </button>
          </div>
        ) : (
          <div className="divide-y divide-secondary-200">
            {configs.map((config) => (
              <div key={config.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`flex-shrink-0 w-2 h-2 rounded-full ${
                        config.is_active ? 'bg-accent-500' : 'bg-secondary-400'
                      }`} />
                      <div>
                        <h3 className="text-sm font-medium text-secondary-900">
                          {config.name || 'Unnamed Configuration'}
                        </h3>
                        <p className="text-sm text-secondary-600">
                          {getScopeDescription(config)}
                        </p>
                        <p className="text-xs text-secondary-500">
                          Action: {config.trigger_action || 'create_issue'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm text-secondary-900">
                        {config.consecutive_failures_threshold} consecutive failures
                      </div>
                      <div className="text-xs text-secondary-500">
                        Max {config.max_issues_per_day} issues/day
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {config.auto_create_issue ? (
                        <CheckCircleIcon className="h-4 w-4 text-accent-500" title="Auto-create enabled" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 text-secondary-400" title="Auto-create disabled" />
                      )}
                      
                      <button
                        onClick={() => handleEdit(config)}
                        className="p-1 text-secondary-400 hover:text-secondary-600"
                        title="Edit configuration"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(config)}
                        className="p-1 text-danger-400 hover:text-danger-600"
                        title="Delete configuration"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Configuration Modal */}
      {showDeleteModal && configToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowDeleteModal(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Configuration</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the configuration <strong>"{configToDelete.name}"</strong>? 
                        This action cannot be undone and will permanently remove this validation-to-issue configuration.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete Configuration
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationIssueSettings;